import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider } from '@repletoryreach/sdk-api/react';
import { AuthProvider, captureImpersonationFromUrl } from '@repletoryreach/sdk-auth';
import { RealtimeProvider } from '@repletoryreach/sdk-realtime';
import { UIProvider } from '@repletoryreach/sdk-ui';
import { ModalsProvider } from '@repletoryreach/sdk-ui/modals';
import { NotificationsProvider, NavigationProgress, notifyWarning } from '@repletoryreach/sdk-ui/feedback';
import { I18nProvider } from './i18n/I18nProvider';
import { FileViewerHost } from './components/FileViewerHost';
import { CATALOGS } from './i18n/catalog';
import { AppRoutes } from './app/routes';
import { PhoneDefaults } from './app/PhoneDefaults';
import '@repletoryreach/sdk-media/styles.css';
import './styles.css';
import './styles/list-row.css';
import './styles/panel.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 }
  }
});

// Same-origin in dev via the Vite proxy ('/api' → gateway, '/hubs' → gateway WS).
const apiBaseUrl = import.meta.env.VITE_API_BASE ?? '/api';
const realtimeBaseUrl = import.meta.env.VITE_REALTIME_BASE ?? '';

// W31: adopt an admin-minted impersonation token from the URL fragment
// (tab-scoped sessionStorage) and scrub it before the router ever mounts.
captureImpersonationFromUrl();

function AppDataTag({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    document.documentElement.setAttribute('data-rr-app', 'tenant');
  }, []);
  return <>{children}</>;
}

/**
 * Session expiry (_UI_improvements/tenant/_app-shell.md §4 item 1).
 *
 * `AuthProvider` only wires the global auth-expiry hook when an `onAuthExpired`
 * prop is supplied. Without it a failed refresh cleared the tokens but never
 * cleared the user, so the shell kept rendering against a stale session while
 * every request 401'd silently. Passing this callback makes the provider call
 * `setUser(null)`, which drops the user through `RequireAuth` to the login page;
 * the toast explains why they landed there.
 *
 * Read straight from the catalog: this fires outside the React tree that owns
 * `useT`, and the locale the user chose is the one they should be told in.
 */
function onAuthExpired() {
  const locale = (localStorage.getItem('rr.tenant.locale') ?? 'en') as keyof typeof CATALOGS;
  const strings = CATALOGS[locale] ?? CATALOGS.en;
  notifyWarning(
    strings['shell.session.expiredBody'] ?? CATALOGS.en['shell.session.expiredBody'],
    strings['shell.session.expiredTitle'] ?? CATALOGS.en['shell.session.expiredTitle']
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Opt in to the v7 behaviours now. Without these two flags React Router
        logs a future-flag warning on every page load, which buries the console
        errors worth reading. */}
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ConfigProvider config={{ apiBaseUrl, realtimeBaseUrl }}>
        <UIProvider defaultColorScheme="auto">
          <AppDataTag>
            <AuthProvider domain="tenant" onAuthExpired={onAuthExpired}>
              <PhoneDefaults>
              <RealtimeProvider>
                <QueryClientProvider client={queryClient}>
                  <I18nProvider>
                    <ModalsProvider>
                      <FileViewerHost>
                        {/* Top-of-page bar driven by sdk-utils' in-flight request count. */}
                        <NavigationProgress />
                        <NotificationsProvider position="bottom-right" />
                        <AppRoutes />
                      </FileViewerHost>
                    </ModalsProvider>
                  </I18nProvider>
                </QueryClientProvider>
              </RealtimeProvider>
              </PhoneDefaults>
            </AuthProvider>
          </AppDataTag>
        </UIProvider>
      </ConfigProvider>
    </BrowserRouter>
  </StrictMode>
);
